import { A, Navigate } from "@solidjs/router";
import { AiOutlineLoading3Quarters } from "solid-icons/ai";
import { type JSX, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import type { BillingStatus } from "../api/types";
import { useEligibility } from "../hooks/useEligibility";
import type { EligibilityAction } from "../utils/eligibility";
import { isBillingEnabled } from "../utils/is-billing-enabled";
import { PaywallInterstitial } from "./PaywallInterstitial";

type Props = {
  action: EligibilityAction;
  children: JSX.Element;
  /** Where to send a user with no selected company (default: company selection). */
  noCompanyRedirect?: string;
  /**
   * How to present an exhausted quota: a full-screen `"block"` interstitial
   * (default) or `"inline"` — let the children render so the surface stays
   * visible (the caller shows a compact `QuotaUpsellBanner` and locks the
   * entry). A degraded billing status always blocks regardless of this.
   */
  quotaTreatment?: "block" | "inline";
  /**
   * When `true`, bypass the guard entirely and render the children unguarded.
   * For a route that is only *conditionally* a guarded action: e.g. `/result`
   * runs a from-scratch search (consuming an offer slot) only when no
   * `job_offer_id` is present — the from-offer variant reuses an existing offer
   * and must stay exempt from the offer-creation quota guard (D6).
   */
  skip?: boolean;
};

function GuardWarningBanner(props: { status: BillingStatus | undefined }) {
  const { t } = useI18n();
  const message = () =>
    props.status === "incomplete"
      ? t("billing.eligibility_guard.warning.incomplete")
      : t("billing.eligibility_guard.warning.grace_period");

  return (
    <div class="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <span>{message()}</span>
      <A
        href="/profile/company/settings/billing"
        class="shrink-0 font-medium underline"
      >
        {t("billing.eligibility_guard.warning.cta")}
      </A>
    </div>
  );
}

/**
 * Preventive paywall guard for a creation / active-search entry point.
 *
 * Order of resolution:
 *  0. `skip` → children unchanged (the route is not a guarded action this time);
 *  1. no selected company → redirect (never a billing interstitial, FLOW edge case);
 *  2. billing/usage still loading → spinner, never the interstitial (A.8 / fail-open);
 *  3. verdict `block` → `PaywallInterstitial`, children (and any AI request) never mount (A.1);
 *     — unless `quotaTreatment="inline"` and the block is a quota exhaustion: then the
 *       children render (the caller surfaces a compact banner + locks the entry instead);
 *  4. verdict `warn` (grace/incomplete) → non-blocking banner + children (D4);
 *  5. otherwise → children unchanged.
 *
 * Fail-open is delegated to `getEligibility` (undefined data → allow), so a paying
 * customer is never blocked on slow/failed billing reads (D7).
 */
export function EligibilityGuard(props: Props) {
  const eligibility = useEligibility(props.action);

  return (
    <Show when={isBillingEnabled() && !props.skip} fallback={props.children}>
      <Show
        when={eligibility.hasCompany()}
        fallback={
          <Navigate href={props.noCompanyRedirect ?? "/profile/company"} />
        }
      >
        <Show
          when={!eligibility.isLoading()}
          fallback={
            <div class="flex items-center justify-center py-20">
              <AiOutlineLoading3Quarters
                class="h-8 w-8 animate-spin text-muted-foreground"
                size={32}
              />
            </div>
          }
        >
          <Show
            when={
              eligibility.verdict().mode !== "block" ||
              (props.quotaTreatment === "inline" &&
                eligibility.verdict().reason === "quota_exhausted")
            }
            fallback={<PaywallInterstitial verdict={eligibility.verdict()} />}
          >
            <Show when={eligibility.verdict().mode === "warn"}>
              <GuardWarningBanner
                status={eligibility.verdict().billingStatus}
              />
            </Show>
            {props.children}
          </Show>
        </Show>
      </Show>
    </Show>
  );
}
